import {
  Box,
  Icon,
  type SortDirection,
  Table,
  Tag,
  Text,
} from '@factorialco/deprecated-design-system'
import orderBy from 'lodash/orderBy'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import EmployeeLabel from 'modules/reviews/components/EmployeeLabel'
import { useGroups } from 'modules/reviews/hooks/useGroups'
import { getGroupName } from 'modules/reviews/lib/getGroupName'
import { getReviewLabel } from 'modules/reviews/lib/getReviewLabel'
import { getType } from 'modules/reviews/lib/reviewQuestionGroupState'
import type QuestionGroup from 'modules/reviews/store/models/QuestionGroup'

interface StateProps {
  questionGroup: QuestionGroup
}

function QuestionGroupState({ questionGroup }: StateProps) {
  const labelType = getType(questionGroup)
  const label = getReviewLabel(questionGroup.attributes.state)

  return (
    <Tag leftItem="circle" capitalize showCircle type={labelType}>
      {label}
    </Tag>
  )
}

interface Props {
  search: string
  questionGroups: QuestionGroup[]
  loading: boolean
  direction: SortDirection
  attribute: string
}

const orderQuestionGroup = (questionGroup: QuestionGroup, attribute: string) => {
  switch (attribute) {
    case 'review_type':
      return getGroupName(questionGroup?.respondentGroup)
    case 'state':
      return getReviewLabel(questionGroup.attributes.state)
    default:
      return questionGroup?.target.fullName()
  }
}

function Body({ questionGroups, search, loading, attribute, direction }: Props) {
  const { navigation } = useNavigation()
  useGroups()

  const filteredQuestionGroups = questionGroups.filter((questionGroup) =>
    questionGroup.target?.matchesFullName(search)
  )
  const sortedQuestionGroups = orderBy(
    filteredQuestionGroups,
    (questionGroup) => orderQuestionGroup(questionGroup, attribute),
    [direction]
  )

  const showBlankSlate = Boolean(!questionGroups.length) || Boolean(!filteredQuestionGroups.length)

  if (loading)
    return (
      <Table.Body>
        <Table.Loading colSpan={10} />
      </Table.Body>
    )

  return (
    <Table.Body>
      {showBlankSlate && (
        <Table.Blank
          colSpan={10}
          text={i18n.t('surveys.review.write_reviews.table.body.no_reviews')}
        />
      )}
      {Boolean(questionGroups.length) &&
        sortedQuestionGroups.map((questionGroup) => {
          const target = questionGroup.target

          if (!target) return null

          const date = questionGroup.attributes.editable_until
          const route =
            questionGroup.isActive || questionGroup.isDraft || questionGroup.isAnswered
              ? navigation.performance.reviews.id.writeReviews.answerQuestionGroupId.fullPath({
                  id: questionGroup.attributes.surveys_form_id,
                  question_group_id: questionGroup.attributes.id,
                })
              : null

          const content = (
            <>
              <Table.Cell colSpan={3}>
                <EmployeeLabel user={target} />
              </Table.Cell>
              <Table.Cell colSpan={2}>
                <Text.Normal>{getGroupName(questionGroup.respondentGroup)}</Text.Normal>
              </Table.Cell>
              <Table.Cell colSpan={2}>
                <Box flexDirection="row">
                  <QuestionGroupState questionGroup={questionGroup} />
                </Box>
              </Table.Cell>
              <Table.Cell colSpan={2}>
                <Text.Normal>{date ? moment(date).format('L') : '-'}</Text.Normal>
              </Table.Cell>
              <Table.Cell>
                <Box alignItems="flexEnd" aria-label="Write review">
                  <Icon.Medium icon="arrow-right" />
                </Box>
              </Table.Cell>
            </>
          )

          if (route) {
            return (
              <Table.Row.Router key={questionGroup.get('id')} to={route}>
                {content}
              </Table.Row.Router>
            )
          }

          return <Table.Row key={questionGroup.get('id')}>{content}</Table.Row>
        })}
    </Table.Body>
  )
}

export default observer(Body)
